import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function DashboardWidgets() {
  const { toast } = useToast();
  const [widgets, setWidgets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data, error } = await supabase
        .from("dashboard_widgets")
        .select("*")
        .eq("user_id", user.user.id)
        .eq("is_visible", true)
        .order("position");

      if (error) throw error;
      setWidgets(data || []);
    } catch (error: any) {
      console.error("Error loading widgets:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading dashboard...</div>;
  }

  if (widgets.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground mb-4">
            No widgets configured. Customize your dashboard.
          </p>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Widget
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {widgets.map((widget) => (
        <WidgetCard key={widget.id} widget={widget} />
      ))}
    </div>
  );
}

function WidgetCard({ widget }: { widget: any }) {
  const [value, setValue] = useState<string>("—");
  const [label, setLabel] = useState<string>("");

  useEffect(() => {
    (async () => {
      try {
        const { data: userRes } = await supabase.auth.getUser();
        const { data: profile } = await supabase
          .from("profiles")
          .select("org_id")
          .eq("id", userRes.user?.id ?? "")
          .maybeSingle();
        const orgId = profile?.org_id;
        if (!orgId) return;

        const type = (widget.widget_type || widget.type || "").toString().toLowerCase();
        if (type.includes("job")) {
          const { count } = await supabase.from("jobs").select("id", { count: "exact", head: true }).eq("org_id", orgId);
          setValue(String(count ?? 0));
          setLabel("Total jobs");
        } else if (type.includes("lead")) {
          const { count } = await supabase.from("leads").select("id", { count: "exact", head: true }).eq("org_id", orgId);
          setValue(String(count ?? 0));
          setLabel("Total leads");
        } else if (type.includes("revenue") || type.includes("invoice")) {
          const { data } = await supabase.from("invoices").select("total_amount, status").eq("org_id", orgId).eq("status", "paid");
          const sum = (data || []).reduce((s: number, r: any) => s + Number(r.total_amount || 0), 0);
          setValue(`$${sum.toLocaleString()}`);
          setLabel("Revenue (paid)");
        } else {
          setLabel(widget.widget_type || "Custom widget");
        }
      } catch (e) {
        console.error("widget load failed", e);
      }
    })();
  }, [widget]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{widget.title}</CardTitle>
          <Button variant="ghost" size="sm">
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        {label && <p className="text-sm text-muted-foreground">{label}</p>}
      </CardContent>
    </Card>
  );
}
